import { useState, useEffect } from "react";
import { api, API } from "@/lib/api";
import CatalogView from "@/components/CatalogView";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Eye, FileDown } from "lucide-react";

export default function PreviewPanel() {
  const [levels, setLevels] = useState([]);
  const [level, setLevel] = useState("");
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const token = localStorage.getItem("thermaco_admin_token");

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const r = await api.get(`/admin/preview-catalog.pdf?level=${encodeURIComponent(level)}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `thermaco_price_list_${level}.pdf`.replace(/[\s()]+/g, "_"); a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not generate PDF"); }
    finally { setPdfBusy(false); }
  };

  useEffect(() => {
    api.get("/admin/meta").then((r) => { setLevels(r.data.levels); setLevel(r.data.levels[0]); });
  }, []);

  useEffect(() => {
    if (!level) return;
    setLoading(true);
    api.get(`/admin/preview-catalog?level=${encodeURIComponent(level)}`)
      .then((r) => setCatalog(r.data))
      .finally(() => setLoading(false));
  }, [level]);

  const total = catalog ? catalog.families.reduce((n, g) => n + g.items.length, 0) : 0;

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-slate-900">Price List Preview</h1>
          <p className="text-slate-500 mt-1">Exactly what a customer at the selected price level sees in the portal.</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs uppercase tracking-[0.1em] text-slate-500">Preview as</span>
          <Select value={level} onValueChange={setLevel}>
            <SelectTrigger className="rounded-sm w-60 bg-white" data-testid="preview-level-select"><SelectValue placeholder="Price level" /></SelectTrigger>
            <SelectContent>{levels.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={downloadPdf} disabled={!level || pdfBusy} className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="download-price-list-pdf">
            <FileDown className="h-4 w-4 mr-2" /> {pdfBusy ? "Building…" : "Download PDF"}
          </Button>
        </div>
      </header>

      <div className="flex items-center gap-2 text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 mb-6" data-testid="preview-banner">
        <Eye className="h-3.5 w-3.5 text-amber-600" /> Preview mode · {loading ? "Loading…" : `${total} products visible to ${level}`}. Archived, orphaned and unclassified products are hidden here, just like in the portal.
      </div>

      {catalog && !loading && (
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-6" data-testid="preview-catalog">
          <CatalogView catalog={catalog} fileUrl={(ref) => `${API}/admin/files/${ref}?auth=${token}`} />
        </div>
      )}
    </div>
  );
}
